// OWMap BSL1 — Globe v2 — disciplined art direction
// Hierarchy: graticule → continent line-art → dormant ticks → active points → featured halos → SELECTED constellation
// Saturation lowered globally; only the selected coord gets full chromatic weight.

const { useRef, useEffect, useImperativeHandle, forwardRef } = React;
const GLOBE_RADIUS = 1;

// Desaturate any THREE.Color toward graphite by mix amount
function desaturate(color, amount = 0.45) {
  const hsl = {};
  color.getHSL(hsl);
  const out = new THREE.Color();
  out.setHSL(hsl.h, hsl.s * (1 - amount), hsl.l * 0.78 + 0.06);
  return out;
}

// Texture cache — avoids recreating 4K canvas on every hot-reload/remount
const _texCache = {};

// ── Earth surface texture ─ minimal, painterly, with graticule baked in ──
function makeEarthTexture(width = 2048, height = 1024) {
  const key = `earth_${width}x${height}`;
  if (_texCache[key]) return _texCache[key];
  const cv = document.createElement("canvas");
  cv.width = width; cv.height = height;
  const ctx = cv.getContext("2d");

  // Deep ocean — very dark, slight tonal variation north/south
  const og = ctx.createLinearGradient(0, 0, 0, height);
  og.addColorStop(0,   "#020610");
  og.addColorStop(0.5, "#04101e");
  og.addColorStop(1,   "#020610");
  ctx.fillStyle = og;
  ctx.fillRect(0, 0, width, height);

  // Stylized continent silhouettes (low contrast, painterly)
  const land = [
    [[18,18],[24,16],[30,16],[34,20],[36,24],[38,30],[34,34],[28,36],[22,32],[18,28]],
    [[28,46],[32,46],[34,52],[34,60],[30,68],[26,70],[22,64],[24,54],[26,48]],
    [[40,12],[46,10],[48,16],[44,20],[38,18]],
    [[50,22],[58,20],[62,24],[60,30],[54,32],[50,28]],
    [[52,34],[60,32],[66,40],[68,48],[64,58],[58,60],[54,52],[52,42]],
    [[62,32],[68,32],[70,40],[66,42],[62,38]],
    [[62,18],[78,16],[88,22],[86,32],[82,36],[74,34],[68,28],[64,24]],
    [[80,38],[86,40],[88,46],[84,48],[80,44]],
    [[70,32],[74,32],[76,40],[72,44],[70,38]],
    [[84,54],[92,54],[94,60],[88,62],[84,58]],
    [[2,72],[98,72],[98,80],[2,80]],
  ];

  // First pass: very subtle filled landmass (graphite warm)
  ctx.fillStyle = "#0a121e";
  for (const poly of land) {
    ctx.beginPath();
    poly.forEach(([px, py], i) => {
      const x = (px / 100) * width;
      const y = (py / 100) * height;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.fill();
  }
  // Second pass: line-art outlines (the painterly edge)
  ctx.strokeStyle = "rgba(180, 195, 215, 0.18)";
  ctx.lineWidth = 1.5;
  for (const poly of land) {
    ctx.beginPath();
    poly.forEach(([px, py], i) => {
      const x = (px / 100) * width;
      const y = (py / 100) * height;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.stroke();
  }

  // Graticule — subtle latitude/longitude grid
  ctx.strokeStyle = "rgba(140, 160, 190, 0.06)";
  ctx.lineWidth = 1;
  // Longitudes every 15°
  for (let lon = 0; lon <= 360; lon += 15) {
    const x = (lon / 360) * width;
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
  }
  // Latitudes every 15°
  for (let lat = 0; lat <= 180; lat += 15) {
    const y = (lat / 180) * height;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
  }
  // Equator + tropics + arctic / antarctic
  ctx.strokeStyle = "rgba(245, 200, 66, 0.10)";
  ctx.lineWidth = 1;
  [90, 90 - 23.5, 90 + 23.5, 90 - 66.5, 90 + 66.5].forEach(lat => {
    const y = (lat / 180) * height;
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
  });
  // Prime meridian + antimeridian
  ctx.strokeStyle = "rgba(245, 200, 66, 0.08)";
  [0, 180].forEach(lon => {
    const x = (lon / 360) * width;
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
  });

  // Axial labels — just numerals at intersections, ultra-low alpha (mono feel)
  ctx.fillStyle = "rgba(180, 200, 220, 0.18)";
  ctx.font = "bold 14px monospace";
  for (let lon = 0; lon < 360; lon += 30) {
    const x = (lon / 360) * width + 4;
    const lonLabel = lon > 180 ? `${360 - lon}°W` : `${lon}°E`;
    ctx.fillText(lonLabel, x, height/2 + 16);
  }

  // Subtle warm city-light specks (rare; restrained)
  ctx.globalCompositeOperation = "lighter";
  for (let i = 0; i < 140; i++) {
    const x = Math.random() * width;
    const y = 0.2 * height + Math.random() * 0.55 * height;
    ctx.fillStyle = `rgba(245, 200, 100, ${Math.random() * 0.10 + 0.02})`;
    ctx.beginPath(); ctx.arc(x, y, Math.random() * 1.0 + 0.3, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalCompositeOperation = "source-over";

  const tex = new THREE.CanvasTexture(cv);
  _texCache[key] = tex;
  return tex;
}

// Soft cloud — kept very subtle
function makeCloudTexture(width = 1024, height = 512) {
  const key = `cloud_${width}x${height}`;
  if (_texCache[key]) return _texCache[key];
  const cv = document.createElement("canvas");
  cv.width = width; cv.height = height;
  const ctx = cv.getContext("2d");
  ctx.globalCompositeOperation = "lighter";
  for (let i = 0; i < 110; i++) {
    const x = Math.random() * width;
    const y = Math.random() * height;
    const r = 60 + Math.random() * 140;
    const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
    grad.addColorStop(0, `rgba(220, 230, 240, ${0.04 + Math.random() * 0.05})`);
    grad.addColorStop(1, "rgba(220,230,240,0)");
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }
  const tex = new THREE.CanvasTexture(cv);
  _texCache[key] = tex;
  return tex;
}

function latLngToVec3(lat, lng, radius = GLOBE_RADIUS) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
     radius * Math.cos(phi),
     radius * Math.sin(phi) * Math.sin(theta),
  );
}

// Halo sprite — single subtle radial, story-gold core only at top of hierarchy.
// Cached by rgb+weight key to avoid redundant canvas creation per featured coord.
function makeHaloTexture(rgb, weight = 0.5) {
  const key = `halo_${rgb[0]}_${rgb[1]}_${rgb[2]}_${(weight * 100) | 0}`;
  if (_texCache[key]) return _texCache[key];
  const cv = document.createElement("canvas");
  cv.width = 256; cv.height = 256;
  const ctx = cv.getContext("2d");
  const grad = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  grad.addColorStop(0,    `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${0.55 * weight})`);
  grad.addColorStop(0.25, `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${0.18 * weight})`);
  grad.addColorStop(0.6,  `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${0.05 * weight})`);
  grad.addColorStop(1,    `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 256);
  const tex = new THREE.CanvasTexture(cv);
  _texCache[key] = tex;
  return tex;
}

// Pulse-ring sprite (single ring outline, used for selected constellation)
function makeRingTexture() {
  const cv = document.createElement("canvas");
  cv.width = 256; cv.height = 256;
  const ctx = cv.getContext("2d");
  ctx.strokeStyle = "rgba(245, 200, 66, 1)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(128, 128, 110, 0, Math.PI * 2);
  ctx.stroke();
  return new THREE.CanvasTexture(cv);
}

// ── Globe component ──
const GlobeScene = forwardRef(function GlobeScene({
  coords, selectedId, playingId, onSelectCoord, onHoverCoord,
  dayNight, motion, variation, cultureFilter,
}, ref) {
  const mountRef = useRef(null);
  const stateRef = useRef({});

  useImperativeHandle(ref, () => ({
    flyTo: (lat, lng) => {
      const s = stateRef.current;
      if (!s.camera) return;
      const target = latLngToVec3(lat, lng, 3.0);
      s.flyAnim = { from: s.camera.position.clone(), to: target, t: 0, dur: 1.6 };
    },
    setCursorLatLngHandler: (fn) => { stateRef.current.onCursorLatLng = fn; },
  }), []);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth || 800;
    const height = mount.clientHeight || 600;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, width / height, 0.1, 100);
    camera.position.set(0, 0.4, 3.0);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // Lights — restrained, single direction, cool ambient
    const sun = new THREE.DirectionalLight(0xfff4d6, 1.0);
    sun.position.set(-3, 1.4, 2.2);
    scene.add(sun);
    scene.add(new THREE.AmbientLight(0x12203a, 0.7));
    const rim = new THREE.DirectionalLight(0xe87a3c, 0.18);
    rim.position.set(2.5, -0.8, -2.2);
    scene.add(rim);

    // Earth
    const earthTex = makeEarthTexture();
    const earthMat = new THREE.MeshPhongMaterial({
      map: earthTex,
      shininess: 4,
      specular: new THREE.Color(0x101828),
      emissive: new THREE.Color(0x040810),
      emissiveIntensity: 0.4,
    });
    const earth = new THREE.Mesh(new THREE.SphereGeometry(GLOBE_RADIUS, 128, 128), earthMat);

    // Atmosphere fresnel (back side)
    const atmoMat = new THREE.ShaderMaterial({
      transparent: true, side: THREE.BackSide, depthWrite: false,
      uniforms: { glowColor: { value: new THREE.Color(0x4a7fb0) }, intensity: { value: 0.85 } },
      vertexShader: `varying vec3 vNormal; void main(){ vNormal=normalize(normalMatrix*normal); gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
      fragmentShader: `varying vec3 vNormal; uniform vec3 glowColor; uniform float intensity;
        void main(){ float f=pow(0.72-dot(vNormal,vec3(0,0,1)),3.2); gl_FragColor=vec4(glowColor,f*intensity); }`,
    });
    const atmo = new THREE.Mesh(new THREE.SphereGeometry(GLOBE_RADIUS * 1.16, 64, 64), atmoMat);

    // Inner rim glow
    const innerGlowMat = new THREE.ShaderMaterial({
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false,
      uniforms: { glowColor: { value: new THREE.Color(0x6aa0d8) }, intensity: { value: 0.4 } },
      vertexShader: `varying vec3 vN; void main(){ vN=normalize(normalMatrix*normal); gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
      fragmentShader: `varying vec3 vN; uniform vec3 glowColor; uniform float intensity;
        void main(){ float r=pow(1.0-max(dot(vN,vec3(0,0,1)),0.0),2.6); gl_FragColor=vec4(glowColor,r*intensity); }`,
    });
    const innerGlow = new THREE.Mesh(new THREE.SphereGeometry(GLOBE_RADIUS * 1.005, 64, 64), innerGlowMat);

    // Cloud
    const cloudTex = makeCloudTexture();
    const cloudMat = new THREE.MeshLambertMaterial({ map: cloudTex, transparent: true, opacity: 0.32, depthWrite: false });
    const clouds = new THREE.Mesh(new THREE.SphereGeometry(GLOBE_RADIUS * 1.012, 64, 64), cloudMat);

    // ── ICON layer — split into two tiers ──────────────────────────────────
    // Tier A (InstancedMesh): dormant + generated ambient dots — one draw call
    // Tier B (individual Mesh): active named coords — animated, raycasted, interactive
    const iconGroup = new THREE.Group();
    const iconObjects = [];  // only active named coords
    const ringTex = makeRingTexture();

    const activeCoordsArr   = coords.filter(c => !c.dormant);
    const ambientCoordsArr  = coords.filter(c => !!c.dormant);

    // ── Tier A: InstancedMesh for ambient/generated dots ─────────────────
    if (ambientCoordsArr.length > 0) {
      const ambientGeom = new THREE.SphereGeometry(0.0032, 5, 5);
      const ambientMat  = new THREE.MeshBasicMaterial({
        transparent: true, opacity: 0.30, depthTest: true, depthWrite: false,
        vertexColors: true,
      });
      const instMesh = new THREE.InstancedMesh(ambientGeom, ambientMat, ambientCoordsArr.length);
      instMesh.renderOrder = 8;
      instMesh.instanceColor = new THREE.InstancedBufferAttribute(
        new Float32Array(ambientCoordsArr.length * 3), 3
      );

      const dummy = new THREE.Object3D(); // reused across all instances — allocate once
      ambientCoordsArr.forEach((c, i) => {
        const pos = latLngToVec3(c.lat, c.lng, GLOBE_RADIUS * 1.012);
        dummy.position.copy(pos);
        dummy.updateMatrix();
        instMesh.setMatrixAt(i, dummy.matrix);
        const col = desaturate(new THREE.Color(CULTURES[c.culture]?.color || '#334455'), 0.6);
        instMesh.instanceColor.setXYZ(i, col.r, col.g, col.b);
      });
      instMesh.instanceMatrix.needsUpdate = true;
      iconGroup.add(instMesh);
      // Store for culture-filter visibility updates
      iconGroup.userData.ambientMesh = instMesh;
      iconGroup.userData.ambientCoords = ambientCoordsArr;
    }

    // ── Tier B: individual Mesh for active named coords ───────────────────
    activeCoordsArr.forEach(c => {
      const culture = CULTURES[c.culture] || { color: '#888' };
      const colorRaw  = new THREE.Color(culture.color);
      const colorSoft = desaturate(colorRaw, 0.5);
      const pos = latLngToVec3(c.lat, c.lng, GLOBE_RADIUS * 1.012);
      const isFeatured = c.heat >= 0.7;

      // Visual dot (small, tight)
      const dotMat = new THREE.MeshBasicMaterial({
        color: colorSoft, transparent: true,
        opacity: isFeatured ? 0.95 : 0.72,
        depthTest: true, depthWrite: false,
      });
      const dotSize = isFeatured ? 0.0085 : 0.006;
      const dot = new THREE.Mesh(new THREE.SphereGeometry(dotSize, 10, 10), dotMat);
      dot.position.copy(pos);
      dot.renderOrder = 10;
      iconGroup.add(dot);

      // Invisible enlarged hit sphere for easier raycasting (2× visual size)
      const hitMat = new THREE.MeshBasicMaterial({ visible: false });
      const hitDot = new THREE.Mesh(new THREE.SphereGeometry(0.022, 6, 6), hitMat);
      hitDot.position.copy(pos);
      iconGroup.add(hitDot);

      // Halo for featured points
      let halo = null;
      if (isFeatured) {
        const rgb = [Math.round(colorSoft.r*255), Math.round(colorSoft.g*255), Math.round(colorSoft.b*255)];
        const haloMat = new THREE.SpriteMaterial({
          map: makeHaloTexture(rgb, 0.7),
          transparent: true, depthTest: false, depthWrite: false,
          blending: THREE.AdditiveBlending, opacity: 0.7,
        });
        halo = new THREE.Sprite(haloMat);
        const sz = 0.04 + (c.heat - 0.7) * 0.18;
        halo.scale.set(sz, sz, 1);
        halo.position.copy(pos);
        halo.renderOrder = 9;
        iconGroup.add(halo);
      }

      // Selected constellation rings
      const rings = [];
      for (let i = 0; i < 3; i++) {
        const m = new THREE.SpriteMaterial({
          map: ringTex, transparent: true, depthTest: false, depthWrite: false,
          blending: THREE.AdditiveBlending, opacity: 0,
          color: new THREE.Color(0xF5C842),
        });
        const sp = new THREE.Sprite(m);
        sp.scale.set(0.06, 0.06, 1);
        sp.position.copy(pos);
        sp.renderOrder = 12;
        rings.push(sp);
        iconGroup.add(sp);
      }

      // Beam
      const beamGeom = new THREE.BufferGeometry().setFromPoints([
        pos.clone().multiplyScalar(1.0),
        pos.clone().multiplyScalar(1.18),
      ]);
      const beamMat = new THREE.LineBasicMaterial({
        color: 0xF5C842, transparent: true, opacity: 0, depthTest: false,
      });
      const beam = new THREE.Line(beamGeom, beamMat);
      beam.renderOrder = 11;
      iconGroup.add(beam);

      iconObjects.push({
        coord: c, dot, hitDot, halo, rings, beam,
        basePos: pos.clone(), color: colorSoft,
        isFeatured, isDormant: false,
      });
    });

    // Star field — graphite + warm sparse
    const starsGeom = new THREE.BufferGeometry();
    const starCount = 1500;
    const starPos = new Float32Array(starCount * 3);
    const starColor = new Float32Array(starCount * 3);
    const starSize = new Float32Array(starCount);
    for (let i = 0; i < starCount; i++) {
      const u = Math.random(); const v = Math.random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const r = 28 + Math.random() * 22;
      starPos[i*3]   = r * Math.sin(phi) * Math.cos(theta);
      starPos[i*3+1] = r * Math.sin(phi) * Math.sin(theta);
      starPos[i*3+2] = r * Math.cos(phi);
      const warm = Math.random() < 0.06;
      starColor[i*3]   = warm ? 1.0 : 0.78 + Math.random()*0.18;
      starColor[i*3+1] = warm ? 0.8  : 0.85 + Math.random()*0.12;
      starColor[i*3+2] = warm ? 0.55 : 1.0;
      starSize[i] = warm ? 0.12 : 0.04 + Math.random()*0.06;
    }
    starsGeom.setAttribute("position", new THREE.BufferAttribute(starPos, 3));
    starsGeom.setAttribute("color",    new THREE.BufferAttribute(starColor, 3));
    const starMat = new THREE.PointsMaterial({
      size: 0.06, vertexColors: true, transparent: true, opacity: 0.7, sizeAttenuation: true,
    });
    const stars = new THREE.Points(starsGeom, starMat);

    // Rotation group — earth + clouds + icons rotate together; atmosphere stays fixed
    const rotationGroup = new THREE.Group();
    rotationGroup.add(earth, clouds, innerGlow, iconGroup);
    rotationGroup.rotation.x = -0.18;
    rotationGroup.rotation.y = -0.4;

    scene.add(stars, atmo, rotationGroup);

    // ── Drag controls ──
    let isDragging = false;
    let lastX = 0, lastY = 0;
    let rotationVel = { x: 0, y: 0 };
    const dom = renderer.domElement;
    dom.style.cursor = "grab";

    function onPointerDown(e) {
      isDragging = true;
      dom.style.cursor = "grabbing";
      lastX = e.clientX; lastY = e.clientY;
      rotationVel = { x: 0, y: 0 };
    }
    function onPointerMove(e) {
      if (!isDragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      rotationGroup.rotation.y += dx * 0.005;
      rotationGroup.rotation.x = Math.max(-1.2, Math.min(1.2, rotationGroup.rotation.x + dy * 0.003));
      rotationVel = { x: dy * 0.003, y: dx * 0.005 };
      lastX = e.clientX; lastY = e.clientY;
    }
    function onPointerUp() { isDragging = false; dom.style.cursor = "grab"; }

    const ray = new THREE.Raycaster();
    ray.params.Points = { threshold: 0.02 };
    const mouse = new THREE.Vector2();
    let downAt = null;

    function pickAt(clientX, clientY) {
      const rect = dom.getBoundingClientRect();
      mouse.x =  ((clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((clientY - rect.top)  / rect.height) * 2 + 1;
      ray.setFromCamera(mouse, camera);
      // Raycast against enlarged hit spheres for reliable mobile/desktop clicking
      const hitDots = iconObjects.map(o => o.hitDot);
      const hits = ray.intersectObjects(hitDots, false);
      if (hits.length === 0) return null;
      return iconObjects.find(o => o.hitDot === hits[0].object);
    }

    function onMouseMove(e) {
      onPointerMove(e);
      if (!isDragging) {
        const hit = pickAt(e.clientX, e.clientY);
        if (hit && onHoverCoord) onHoverCoord(hit.coord, e.clientX, e.clientY);
        else if (onHoverCoord) onHoverCoord(null);
        dom.style.cursor = hit ? "pointer" : "grab";
      }

      // Hover-aware coordinate readout (lat/lng under cursor on globe)
      const r2 = dom.getBoundingClientRect();
      const mx =  ((e.clientX - r2.left) / r2.width) * 2 - 1;
      const my = -((e.clientY - r2.top)  / r2.height) * 2 + 1;
      const ray2 = new THREE.Raycaster();
      ray2.setFromCamera({x: mx, y: my}, camera);
      const sphere = new THREE.Sphere(new THREE.Vector3(0,0,0), GLOBE_RADIUS);
      const target = new THREE.Vector3();
      const intersected = ray2.ray.intersectSphere(sphere, target);
      if (intersected) {
        const local = target.clone().applyMatrix4(rotationGroup.matrixWorld.clone().invert());
        const lat = 90 - Math.acos(local.y / GLOBE_RADIUS) * 180 / Math.PI;
        const lng = (Math.atan2(local.z, -local.x) * 180 / Math.PI) - 180;
        if (stateRef.current.onCursorLatLng) stateRef.current.onCursorLatLng({ lat, lng });
      } else {
        if (stateRef.current.onCursorLatLng) stateRef.current.onCursorLatLng(null);
      }
    }

    function onClick(e) {
      if (!downAt) return;
      const dx = Math.abs(e.clientX - downAt.x), dy = Math.abs(e.clientY - downAt.y);
      if (dx > 5 || dy > 5) return;
      const hit = pickAt(e.clientX, e.clientY);
      if (hit) onSelectCoord && onSelectCoord(hit.coord);
    }
    function onMouseDown(e) { downAt = { x: e.clientX, y: e.clientY }; onPointerDown(e); }

    dom.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onPointerUp);
    dom.addEventListener("click", onClick);
    dom.addEventListener("touchstart", e => { const t=e.touches[0]; onMouseDown({clientX:t.clientX,clientY:t.clientY}); }, { passive: true });
    dom.addEventListener("touchmove",  e => { const t=e.touches[0]; onPointerMove({clientX:t.clientX,clientY:t.clientY}); }, { passive: true });
    dom.addEventListener("touchend", onPointerUp);

    function onResize() {
      const w = mount.clientWidth, h = mount.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    // ── Animate ──
    let time = 0; let raf = 0;
    const clock = new THREE.Clock();

    function tick() {
      const dt = clock.getDelta();
      time += dt;
      const motionFactor = stateRef.current.motion ?? 1;

      if (!isDragging) {
        rotationGroup.rotation.y += 0.0035 * motionFactor;
        rotationGroup.rotation.x += rotationVel.x * 0.85;
        rotationGroup.rotation.y += rotationVel.y * 0.85;
        rotationVel.x *= 0.92; rotationVel.y *= 0.92;
      }
      clouds.rotation.y += 0.0006 * motionFactor;

      // ICON updates with hierarchy
      iconObjects.forEach((o, i) => {
        const isSelected = o.coord.id === stateRef.current.selectedId;
        const isPlaying  = o.coord.id === stateRef.current.playingId;

        if (o.coord.dormant) return;

        // Featured halos: gentle breath
        if (o.halo) {
          const phase = (time * 0.5 + i * 0.4) * motionFactor;
          const pulse = 1 + Math.sin(phase) * 0.10;
          const baseSz = 0.04 + (o.coord.heat - 0.7) * 0.18;
          o.halo.scale.set(baseSz * pulse, baseSz * pulse, 1);
        }

        // Dot subtle pulse — much more restrained than before
        const dotPhase = (time * 0.4 + i * 0.6) * motionFactor;
        const dotPulse = 1 + Math.sin(dotPhase) * 0.06;
        o.dot.scale.setScalar(dotPulse);

        // Selected constellation — three rings expanding outward
        if (isSelected || isPlaying) {
          o.rings.forEach((r, k) => {
            const phase = (time * 0.7 + k * 0.66) % 1.5;
            const t01 = phase / 1.5;
            const sz = 0.025 + t01 * 0.16;
            r.scale.set(sz, sz, 1);
            r.material.opacity = (1 - t01) * (isPlaying ? 0.9 : 0.55);
          });
          o.beam.material.opacity = isPlaying ? 0.6 : 0.35;
          // Selected dot bigger
          o.dot.scale.setScalar(dotPulse * 1.8);
          // Override dot color to story-gold for emphasis
          o.dot.material.color.set(isPlaying ? 0xE87A3C : 0xF5C842);
        } else {
          o.rings.forEach(r => { r.material.opacity = 0; });
          o.beam.material.opacity = 0;
          o.dot.material.color.copy(o.color);
        }
      });

      // Stars twinkle slowly
      starMat.opacity = 0.6 + Math.sin(time * 0.25) * 0.08;

      // Camera fly
      if (stateRef.current.flyAnim) {
        const a = stateRef.current.flyAnim;
        a.t += dt;
        const k = Math.min(1, a.t / a.dur);
        const ease = 1 - Math.pow(1 - k, 3);
        camera.position.lerpVectors(a.from, a.to, ease);
        camera.lookAt(0, 0, 0);
        if (k >= 1) stateRef.current.flyAnim = null;
      }

      // Day/night intensity
      const dn = stateRef.current.dayNight ?? 0.4;
      sun.intensity = 0.4 + dn * 1.0;
      earthMat.emissiveIntensity = 0.4 - dn * 0.25;
      cloudMat.opacity = 0.22 + dn * 0.25;

      // Variation B (avant) — atmosphere hue cycle, stronger inner glow
      if (stateRef.current.variation === "avant") {
        atmoMat.uniforms.glowColor.value.setHSL(0.62 + Math.sin(time*0.18)*0.08, 0.35, 0.42);
        innerGlowMat.uniforms.glowColor.value.setHSL(0.05 + Math.sin(time*0.13)*0.04, 0.5, 0.5);
        innerGlowMat.uniforms.intensity.value = 0.5 + Math.sin(time*0.4)*0.15;
        atmoMat.uniforms.intensity.value = 1.05;
      } else {
        atmoMat.uniforms.glowColor.value.set(0x4a7fb0);
        innerGlowMat.uniforms.glowColor.value.set(0x6aa0d8);
        innerGlowMat.uniforms.intensity.value = 0.4;
        atmoMat.uniforms.intensity.value = 0.85;
      }

      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    }
    tick();

    stateRef.current = {
      scene, camera, renderer, iconObjects,
      ambientMesh: iconGroup.userData.ambientMesh || null,
      ambientCoords: iconGroup.userData.ambientCoords || [],
      motion: 1, dayNight: 0.4, variation: "main", selectedId: null, playingId: null,
      onCursorLatLng: null,
    };

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      dom.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onPointerUp);
      dom.removeEventListener("click", onClick);
      try { mount.removeChild(dom); } catch (_) {}
      renderer.dispose();
      earthTex.dispose();
      cloudTex.dispose();
      ringTex.dispose();
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    stateRef.current.motion = motion;
    stateRef.current.dayNight = dayNight;
    stateRef.current.variation = variation;
    stateRef.current.selectedId = selectedId;
    stateRef.current.playingId = playingId;
  }, [motion, dayNight, variation, selectedId, playingId]);

  useEffect(() => {
    const objs = stateRef.current.iconObjects;
    if (!objs) return;
    const noFilter = !cultureFilter || cultureFilter.length === 0;

    // Active named coords — toggle dot + halo
    objs.forEach(o => {
      const visible = noFilter || cultureFilter.includes(o.coord.culture);
      o.dot.visible = visible;
      o.hitDot.visible = visible;
      if (o.halo) o.halo.visible = visible;
    });

    // Ambient InstancedMesh — toggle per-instance scale (scale to 0 = hidden)
    const ambientMesh   = stateRef.current.ambientMesh;
    const ambientCoords = stateRef.current.ambientCoords;
    if (ambientMesh && ambientCoords) {
      const dummy = new THREE.Object3D(); // reused across all instances
      ambientCoords.forEach((c, i) => {
        const visible = noFilter || cultureFilter.includes(c.culture);
        const pos = latLngToVec3(c.lat, c.lng, GLOBE_RADIUS * 1.012);
        dummy.position.copy(pos);
        dummy.scale.setScalar(visible ? 1 : 0);
        dummy.updateMatrix();
        ambientMesh.setMatrixAt(i, dummy.matrix);
      });
      ambientMesh.instanceMatrix.needsUpdate = true;
    }
  }, [cultureFilter]);

  return <div ref={mountRef} style={{ width: "100%", height: "100%", position: "relative" }} />;
});

window.GlobeScene = GlobeScene;
window.latLngToVec3 = latLngToVec3;
